/**
 * Owner: Masa Bayu
 * File: PrivateAuthorSidebar.tsx
 * Date:Time: 2026-10-19 01:40
 * Description: Sidebar dashboard PRIVAT §467-C — logo QX Author, 9 navigasi
 *   (setiap satu laluan nyata / URL qxeditor nyata), kad ilustrasi gunung
 *   "Write • Publish • Inspire" + hak cipta. Item 'Reviews' dibuang: tiada
 *   hujung ulasan di hub, jadi pautannya akan menjadi affordance mati.
 *   §502: item pertama ialah Home → '/' (skrin home qxread). Ia dahulu
 *   'Dashboard' → /author/dashboard, iaitu halaman itu sendiri, jadi
 *   tap-nya mati; logo brand turut dibuat boleh klik ke '/'.
 */

import type { ReactElement, ReactNode } from 'react';
import { QX_TAGLINE } from '../../brand/brand-assets';
import { LandingIcon } from '../../home/marketplace/landing/LandingIcons';
import { STUDIO_ENTRY } from '../author-production-identity';
import type { PrivateAuthorLabels, PrivateNavId } from './private-author-labels';

interface NavItem {
  readonly id: PrivateNavId;
  readonly icon: ReactElement;
  readonly target: string;
  readonly external?: boolean;
  readonly chevron?: boolean;
}

function Stroke({
  children,
  size = 18,
}: {
  readonly children: ReactNode;
  readonly size?: number;
}): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export interface PrivateAuthorSidebarProps {
  readonly activeId: string;
  readonly onNavigate: (path: string) => void;
  readonly la: PrivateAuthorLabels;
}

export function PrivateAuthorSidebar({
  activeId,
  onNavigate,
  la,
}: PrivateAuthorSidebarProps): ReactElement {
  const items: readonly NavItem[] = [
    {
      // §502 — rumah mesti membuka skrin home qxread. Item 'dashboard' dahulu
      // menghantar ke /author/dashboard iaitu halaman yang sedang dipaparkan,
      // jadi tap-nya tidak melakukan apa-apa. Ukuran dom: 0 kontrol dashboard
      // desktop yang bernama "Home", dan logo brand pula <div> tanpa tindakan.
      id: 'home',
      icon: (
        <Stroke>
          <path d="M3 11l9-7 9 7" />
          <path d="M5 10v10h14V10" />
        </Stroke>
      ),
      target: '/',
    },
    {
      id: 'myBooks',
      icon: (
        <Stroke>
          <path d="M4 5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 1-2-2z" />
          <path d="M9 3v18" />
        </Stroke>
      ),
      target: '/author/publications',
      chevron: true,
    },
    {
      id: 'createEbook',
      icon: (
        <Stroke>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v8M8 12h8" />
        </Stroke>
      ),
      target: STUDIO_ENTRY,
    },
    {
      id: 'manuscripts',
      icon: (
        <Stroke>
          <path d="M7 3h8l4 4v14H7z" />
          <path d="M15 3v4h4" />
          <path d="M10 12h6M10 16h6" />
        </Stroke>
      ),
      target: STUDIO_ENTRY,
    },
    {
      id: 'sales',
      icon: (
        <Stroke>
          <path d="M4 20V8M10 20V4M16 20v-8M21 20H3" />
        </Stroke>
      ),
      target: '/author/sales',
    },
    {
      id: 'promotions',
      icon: (
        <Stroke>
          <path d="M4 11l12-6v14L4 13z" />
          <path d="M4 11v3M18 8a3 3 0 0 1 0 6" />
        </Stroke>
      ),
      target: '/author/storefront',
    },
    {
      id: 'reports',
      icon: (
        <Stroke>
          <path d="M5 21V9l5-5 9 3v13z" />
          <path d="M9 13h6M9 17h6" />
        </Stroke>
      ),
      target: '/author/earnings',
    },
    {
      id: 'profile',
      icon: (
        <Stroke>
          <circle cx="12" cy="8" r="3.6" />
          <path d="M5 20a7 7 0 0 1 14 0" />
        </Stroke>
      ),
      target: '/author/settings',
    },
    {
      id: 'help',
      icon: (
        <Stroke>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.5 9.5A2.5 2.5 0 1 1 12 13v1.5" />
          <path d="M12 17.5v.5" />
        </Stroke>
      ),
      target: 'mailto:support@qxread.com',
      external: true,
    },
  ];

  return (
    <aside className="qxp__sidebar" aria-label={la.navAria}>
      <button
        type="button"
        className="qxp__brand"
        aria-label={la.homeAria}
        onClick={() => {
          onNavigate('/');
        }}
      >
        <img src="/brand/qx.png" alt="QX" width="28" height="28" />
        <span className="qxp__brand-tag">
          <strong>{QX_TAGLINE.top}</strong>
          <small>{QX_TAGLINE.bottom}</small>
        </span>
      </button>
      <nav className="qxp__nav">
        {items.map((item) =>
          item.external === true ? (
            <a
              key={item.id}
              className="qxp__navitem"
              href={item.target}
              {...(item.target.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})}
            >
              {item.icon}
              <span>{la.nav[item.id]}</span>
            </a>
          ) : (
            <button
              key={item.id}
              type="button"
              className="qxp__navitem"
              data-active={activeId === item.id}
              aria-current={activeId === item.id ? 'page' : undefined}
              onClick={() => {
                onNavigate(item.target);
              }}
            >
              {item.icon}
              <span>{la.nav[item.id]}</span>
              {item.chevron === true ? (
                <em aria-hidden="true">
                  <LandingIcon name="chevronRight" size={12} />
                </em>
              ) : null}
            </button>
          ),
        )}
      </nav>
      <div className="qxp__side-foot" aria-hidden="true">
        <img className="qxp__side-art" src="/author/banner.png" alt="" />
        <div className="qxp__side-card">
          <p className="qxp__side-motto">{la.sidebarMotto}</p>
          <p className="qxp__side-sub">{la.sidebarSub}</p>
          <img className="qxp__side-logo" src="/brand/qx.png" alt="" />
          <p className="qxp__side-copy">{la.copyright}</p>
        </div>
      </div>
    </aside>
  );
}
